import { SmartReportsItem } from 'models/projectReports'
import { useCallback, useState } from 'react'

/** Identity of a report row within the cache. */
const getRowKey = (row: SmartReportsItem) =>
    `${row.reportType}-${row.division}-${row.section}`

/** Merges freshly fetched rows into the cache, skipping rows already cached. */
const mergeReportRows = (
    cachedRows: SmartReportsItem[] | undefined,
    newRows: SmartReportsItem[]
): SmartReportsItem[] => {
    const cachedKeys = new Set((cachedRows ?? []).map(getRowKey))

    return [
        ...(cachedRows ?? []),
        ...newRows.filter((row) => !cachedKeys.has(getRowKey(row))),
    ]
}

/** The distinct report types present in a set of rows. */
const getReportTypes = (rows: SmartReportsItem[]): string[] => [
    ...new Set(rows.map((row) => row.reportType)),
]

/** Adds report types to the generated list, keeping it duplicate-free. */
const addTypes = (generatedTypes: string[], typesToAdd: string[]): string[] => [
    ...new Set([...generatedTypes, ...typesToAdd]),
]

interface UseReportsCacheReturn {
    /** Every row fetched so far, across all report types. */
    cachedRows: SmartReportsItem[] | undefined
    /** Report types that have finished generating at least once. */
    generatedTypes: string[]
    /** Merges freshly fetched rows in and marks their report types generated. */
    addRows: (newRows: SmartReportsItem[]) => void
    /**
     * Marks report types generated without adding rows.
     *
     * Needed because a type can legitimately return nothing — an empty workspace —
     * and leaving it unmarked makes {@link UseReportsCacheReturn.getTypesToFetch}
     * keep asking for it forever (Bug 2654170).
     */
    markGenerated: (reportTypes: string[]) => void
    /**
     * Drops cached rows for the report types being manually regenerated, and marks
     * them ungenerated, so the fresh results replace them instead of being deduped
     * away. Types that were never generated keep their rows — nothing is stale.
     */
    clearForRegeneration: (reportTypes: string[]) => void
    /** Empties the cache, as when the user clears the report-type filter. */
    reset: () => void
    /** The cached rows belonging to the given report types. */
    getRowsForTypes: (reportTypes: string[]) => SmartReportsItem[]
    /**
     * Which of the selected report types still have to be fetched: absent from the
     * cache **and** never generated before.
     *
     * A type that was generated but is missing from the cache is deliberately
     * excluded — the user has to regenerate it by hand. An empty result therefore
     * means the selection can be served entirely from the cache, which is also the
     * condition that closes the progress dialog.
     */
    getTypesToFetch: (selectedReportTypes: string[]) => string[]
}

/**
 * Owns the project Reports client-side cache: every row fetched so far, plus which
 * report types have already been generated.
 *
 * Those two pieces of state only make sense together — the reconciliation rules
 * between them and the user's current selection decide what gets fetched, what is
 * served from memory, and when the progress dialog can close.
 */
export const useReportsCache = (): UseReportsCacheReturn => {
    const [cachedRows, setCachedRows] = useState<SmartReportsItem[]>()
    const [generatedTypes, setGeneratedTypes] = useState<string[]>([])

    const addRows = useCallback((newRows: SmartReportsItem[]) => {
        setCachedRows((prevRows) => mergeReportRows(prevRows, newRows))
        setGeneratedTypes((prevTypes) =>
            addTypes(prevTypes, getReportTypes(newRows))
        )
    }, [])

    const markGenerated = useCallback((reportTypes: string[]) => {
        setGeneratedTypes((prevTypes) => addTypes(prevTypes, reportTypes))
    }, [])

    const clearForRegeneration = useCallback(
        (reportTypes: string[]) => {
            setCachedRows((prevRows) =>
                prevRows?.filter(
                    (row) =>
                        !reportTypes.includes(row.reportType) ||
                        !generatedTypes.includes(row.reportType)
                )
            )
            setGeneratedTypes((prevTypes) =>
                prevTypes.filter((type) => !reportTypes.includes(type))
            )
        },
        [generatedTypes]
    )

    const reset = useCallback(() => {
        setCachedRows(undefined)
        setGeneratedTypes([])
    }, [])

    const getRowsForTypes = useCallback(
        (reportTypes: string[]) =>
            (cachedRows ?? []).filter((row) =>
                reportTypes.includes(row.reportType)
            ),
        [cachedRows]
    )

    const getTypesToFetch = useCallback(
        (selectedReportTypes: string[]) => {
            const typesWithData = new Set(
                getReportTypes(
                    (cachedRows ?? []).filter((row) =>
                        selectedReportTypes.includes(row.reportType)
                    )
                )
            )

            return selectedReportTypes.filter(
                (type) =>
                    !typesWithData.has(type) && !generatedTypes.includes(type)
            )
        },
        [cachedRows, generatedTypes]
    )

    return {
        cachedRows,
        generatedTypes,
        addRows,
        markGenerated,
        clearForRegeneration,
        reset,
        getRowsForTypes,
        getTypesToFetch,
    }
}
